import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  Package, Clock, CheckCircle, Truck, MapPin, Phone, 
  RefreshCw, ArrowRight, User, DollarSign, Calendar,
  AlertCircle, Navigation, PhoneCall, X, ShoppingBag,
  CreditCard, Wallet, Receipt
} from 'lucide-react'
import { api } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'
import { showOfflineToast } from '../../lib/toastManager'

interface OrderItem {
  id: string
  quantity: number
  quantity_delivered?: number
  unit_price: number
  is_paid?: boolean
  product: {
    id: string
    name: string
    image_url?: string
  }
  variant?: {
    variant_name: string
  }
}

interface Order {
  id: string
  store_id: string
  customer_name: string
  customer_phone: string
  customer_email?: string
  delivery_address?: string
  status: 'pending' | 'preparing' | 'ready' | 'ready_for_pickup' | 'picked_up' | 'delivering' | 'delivered' | 'cancelled'
  total_amount: number
  delivery_fee: number
  estimated_delivery_time?: number
  assigned_delivery_id?: string
  created_at: string
  items: OrderItem[]
  notes?: string
}

const DeliveryDashboard = () => {
  const navigate = useNavigate()
  const { storeId } = useParams<{ storeId?: string }>()
  const { user } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)
  const [deliveryAvailable, setDeliveryAvailable] = useState<boolean>(true)
  const [updatingAvailability, setUpdatingAvailability] = useState(false)

  const currentStoreId = storeId || user?.storeId

  useEffect(() => {
    if (currentStoreId && user?.id) {
      loadOrders()
      loadDeliveryAvailability()
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadOrders()
        loadDeliveryAvailability()
      }, 30000)
      return () => clearInterval(interval)
    }
  }, [currentStoreId, user?.id])

  const loadOrders = async () => {
    if (!currentStoreId || !user?.id) return

    const cacheKey = `delivery_dashboard_orders_${currentStoreId}_${user.id}`
    
    // Intentar cargar desde cache primero
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setOrders(cachedOrders)
      setLoading(false)
      setRefreshing(false)
    }

    try {
      setRefreshing(true)
      const response = await api.get(`/orders/store/${currentStoreId}?limit=100`)
      if (response.data.success) {
        const allOrders = (response.data.data || []).map((order: any) => ({
          id: order.id,
          store_id: order.store_id || order.storeId,
          customer_name: order.customer_name || order.customerName,
          customer_phone: order.customer_phone || order.customerPhone,
          customer_email: order.customer_email || order.customerEmail,
          delivery_address: order.delivery_address || order.deliveryAddress,
          status: order.status,
          total_amount: Number(order.total_amount || order.totalAmount) || 0,
          delivery_fee: Number(order.delivery_fee || order.deliveryFee) || 0,
          estimated_delivery_time: Number(order.estimated_delivery_time || order.estimatedDeliveryTime) || 0,
          assigned_delivery_id: order.assigned_delivery_id || order.assignedDeliveryId,
          created_at: order.created_at || order.createdAt,
          items: (order.items || []).map((item: any) => ({
            ...item,
            is_paid: item.is_paid !== false, // Default to true
            quantity_delivered: item.quantity_delivered || item.quantity
          })),
          notes: order.notes
        }))

        // Filtrar solo pedidos asignados explícitamente a este repartidor
        const myOrders = allOrders.filter((order: Order) => {
          const matchesDelivery = order.assigned_delivery_id === user?.id
          const matchesStatus = ['ready', 'ready_for_pickup', 'picked_up', 'delivering'].includes(order.status)
          return matchesDelivery && matchesStatus
        })

        setOrders(myOrders)
        
        // Guardar en cache (30 segundos de TTL - muy dinámico pero cache ayuda)
        setCache(cacheKey, myOrders, 30 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar pedidos')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handlePickup = async (orderId: string) => {
    try {
      setUpdatingStatus(orderId)
      const response = await api.pickupOrder(orderId)
      
      if (response.success) {
        toast.success('Pedido marcado como recogido')
        await loadOrders()
      } else {
        toast.error(response.message || 'Error al marcar pedido como recogido')
      }
    } catch (error: any) {
      console.error('Error picking up order:', error)
      toast.error(error.response?.data?.message || 'Error al marcar pedido como recogido')
    } finally {
      setUpdatingStatus(null)
    }
  }

  const handleStartDelivery = async (orderId: string) => {
    try {
      setUpdatingStatus(orderId)
      const response = await api.startDelivery(orderId)
      
      if (response.success) {
        toast.success('Entrega iniciada')
        await loadOrders()
        await loadDeliveryAvailability()
      } else {
        toast.error(response.message || 'Error al iniciar entrega')
      }
    } catch (error: any) {
      console.error('Error starting delivery:', error)
      toast.error(error.response?.data?.message || 'Error al iniciar entrega')
    } finally {
      setUpdatingStatus(null)
    }
  }


  const loadDeliveryAvailability = async () => {
    try {
      const response = await api.getProfile()
      if (response.success && response.data) {
        setDeliveryAvailable(response.data.delivery_available !== false)
      }
    } catch (error: any) {
      console.error('Error loading delivery availability:', error)
    }
  }

  const handleToggleAvailability = async () => {
    try {
      setUpdatingAvailability(true)
      const newAvailability = !deliveryAvailable
      const response = await api.updateDeliveryAvailability(newAvailability)
      
      if (response.success) {
        setDeliveryAvailable(newAvailability)
        toast.success(`Estado actualizado: ${newAvailability ? 'Disponible' : 'Ocupado'}`)
      } else {
        toast.error(response.message || 'Error al actualizar disponibilidad')
      }
    } catch (error: any) {
      console.error('Error updating availability:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar disponibilidad')
    } finally {
      setUpdatingAvailability(false)
    }
  }

  const handleViewDetails = (order: Order) => {
    navigate(`/store/delivery/orders/${order.id}`)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'picked_up': return 'bg-blue-100 text-blue-800 border-blue-300'
      case 'delivering': return 'bg-purple-100 text-purple-800 border-purple-300'
      case 'delivered': return 'bg-green-100 text-green-800 border-green-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'Lista para Recoger'
      case 'picked_up': return 'Recogida'
      case 'delivering': return 'En Camino'
      case 'delivered': return 'Entregado'
      default: return status
    }
  }

  // Check if there's an order in delivering status
  const hasActiveDelivery = orders.some(o => o.status === 'delivering' && o.assigned_delivery_id === user?.id)

  // Filter orders by status
  const readyForPickup = orders.filter(o => o.status === 'ready_for_pickup' || o.status === 'ready')
  const pickedUp = orders.filter(o => o.status === 'picked_up')
  const delivering = orders.filter(o => o.status === 'delivering')

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pt-20 pb-24">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600"></div>
              <div className="absolute inset-0 animate-spin rounded-full h-16 w-16 border-4 border-transparent border-r-indigo-600" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 pt-4 lg:pt-8 pb-24">
      <div className="max-w-md lg:max-w-7xl mx-auto px-4 lg:px-8">
        {/* Modern Header with Glassmorphism */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] shadow-2xl shadow-black/5 border border-white/60 p-6 lg:p-8 mb-6 lg:mb-8 sticky top-4 z-10">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Entregas
              </h1>
              <p className="text-sm lg:text-base text-gray-500 mt-1 font-medium">Panel de repartidor</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/store/delivery/wallet')}
                className="p-3 lg:p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl hover:from-emerald-100 hover:to-green-100 transition-all shadow-lg shadow-emerald-500/10 active:scale-95"
                title="Mi Cartera"
              >
                <Wallet className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
              </button>
              <button
                onClick={loadOrders}
                disabled={refreshing}
                className="p-3 lg:p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl hover:from-blue-100 hover:to-indigo-100 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/10 active:scale-95"
              >
                <RefreshCw className={`w-5 h-5 lg:w-6 lg:h-6 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Availability Toggle - Modern Design */}
          <button
            onClick={handleToggleAvailability}
            disabled={updatingAvailability || hasActiveDelivery}
            className={`w-full lg:w-auto lg:px-8 flex items-center justify-center gap-3 px-5 py-4 lg:py-5 rounded-2xl font-bold text-base lg:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl active:scale-[0.98] ${
              deliveryAvailable
                ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-emerald-500/40'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-amber-500/40'
            }`}
          >
            {updatingAvailability ? (
              <RefreshCw className="w-5 h-5 lg:w-6 lg:h-6 animate-spin" />
            ) : (
              <>
                {deliveryAvailable ? (
                  <>
                    <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span>Disponible</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span>Ocupado</span>
                  </>
                )}
              </>
            )}
          </button>
        </div>

        {/* Stats Cards - Modern Design */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-6 mb-6 lg:mb-8">
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl lg:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-5 lg:p-6 text-center hover:shadow-2xl transition-all">
            <div className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Package className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{readyForPickup.length}</p>
            <p className="text-xs lg:text-sm text-gray-600 mt-1.5 font-medium">Listas</p>
          </div>
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl lg:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-5 lg:p-6 text-center hover:shadow-2xl transition-all">
            <div className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <ShoppingBag className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{pickedUp.length}</p>
            <p className="text-xs lg:text-sm text-gray-600 mt-1.5 font-medium">Recogidas</p>
          </div>
          <div className="bg-white/70 backdrop-blur-2xl rounded-2xl lg:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-5 lg:p-6 text-center hover:shadow-2xl transition-all">
            <div className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-2xl lg:rounded-3xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Truck className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{delivering.length}</p>
            <p className="text-xs lg:text-sm text-gray-600 mt-1.5 font-medium">En Camino</p>
          </div>
        </div>

        {/* Orders List - Modern Design - Grid Layout for Desktop */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0 space-y-6">
          {readyForPickup.length > 0 && (
            <div className="mb-6 lg:mb-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6 px-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-amber-500"></div>
                Listas para Recoger
              </h2>
              <div className="space-y-4 lg:space-y-5">
                {readyForPickup.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onViewDetails={handleViewDetails}
                    onPickup={handlePickup}
                    onStartDelivery={handleStartDelivery}
                    updatingStatus={updatingStatus}
                    disabled={hasActiveDelivery}
                  />
                ))}
              </div>
            </div>
          )}

          {pickedUp.length > 0 && (
            <div className="mb-6 lg:mb-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6 px-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-blue-500"></div>
                Recogidas
              </h2>
              <div className="space-y-4 lg:space-y-5">
                {pickedUp.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onViewDetails={handleViewDetails}
                    onPickup={handlePickup}
                    onStartDelivery={handleStartDelivery}
                    updatingStatus={updatingStatus}
                    disabled={hasActiveDelivery}
                  />
                ))}
              </div>
            </div>
          )}

          {delivering.length > 0 && (
            <div className="mb-6 lg:mb-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6 px-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-purple-500"></div>
                En Camino
              </h2>
              <div className="space-y-4 lg:space-y-5">
                {delivering.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onViewDetails={handleViewDetails}
                    onPickup={handlePickup}
                    onStartDelivery={handleStartDelivery}
                    updatingStatus={updatingStatus}
                    disabled={false}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {orders.length === 0 && !loading && (
          <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-2xl shadow-black/5 border border-white/60 p-16 lg:p-24 text-center">
            <div className="w-20 h-20 lg:w-24 lg:h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <Package className="w-10 h-10 lg:w-12 lg:h-12 text-gray-400" />
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">No hay entregas asignadas</h3>
            <p className="text-sm lg:text-base text-gray-500 font-medium">
              Espera a que te asignen entregas
            </p>
          </div>
        )}

      </div>
    </div>
  )
}

// Order Card Component - iPhone style
const OrderCard = ({ 
  order, 
  onViewDetails, 
  onPickup, 
  onStartDelivery, 
  updatingStatus,
  disabled 
}: {
  order: Order
  onViewDetails: (order: Order) => void
  onPickup: (orderId: string) => void
  onStartDelivery: (orderId: string) => void
  updatingStatus: string | null
  disabled: boolean
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'bg-amber-100 text-amber-800'
      case 'picked_up': return 'bg-blue-100 text-blue-800'
      case 'delivering': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ready_for_pickup': return 'Lista para Recoger'
      case 'picked_up': return 'Recogida'
      case 'delivering': return 'En Camino'
      default: return status
    }
  }

  return (
    <div 
      className="bg-white/70 backdrop-blur-2xl rounded-[2rem] lg:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-6 lg:p-8 active:scale-[0.98] transition-all hover:shadow-2xl cursor-pointer"
      onClick={() => onViewDetails(order)}
    >
      <div className="flex items-start justify-between mb-4 lg:mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 lg:gap-4 mb-3 lg:mb-4">
            <span className="text-xs lg:text-sm font-bold text-gray-400 tracking-wider">#{order.id.slice(0, 8).toUpperCase()}</span>
            <span className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-xl lg:rounded-2xl text-xs lg:text-sm font-bold ${getStatusColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
          </div>
          <div className="space-y-2.5 lg:space-y-3">
            <div className="flex items-center gap-2.5 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                <User className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
              </div>
              <span className="text-base lg:text-lg font-bold text-gray-900">{order.customer_name}</span>
            </div>
            {order.delivery_address && (
              <div className="flex items-start gap-2.5 lg:gap-3">
                <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
                  <MapPin className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                </div>
                <span className="text-sm lg:text-base text-gray-600 line-clamp-1 lg:line-clamp-2 font-medium">{order.delivery_address}</span>
              </div>
            )}
            <div className="flex items-center gap-2.5 lg:gap-3">
              <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
                <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600" />
              </div>
              <span className="text-lg lg:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                RD${(order.total_amount + order.delivery_fee).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex gap-3 lg:gap-4 mt-5 lg:mt-6">
        {(order.status === 'ready_for_pickup' || order.status === 'ready') && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onPickup(order.id)
            }}
            disabled={updatingStatus === order.id || disabled}
            className="flex-1 px-5 lg:px-6 py-3.5 lg:py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl lg:rounded-3xl font-bold text-sm lg:text-base shadow-xl shadow-amber-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
          >
            {updatingStatus === order.id ? 'Marcando...' : 'Marcar Recogida'}
          </button>
        )}
        {order.status === 'picked_up' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onStartDelivery(order.id)
            }}
            disabled={updatingStatus === order.id || disabled}
            className="flex-1 px-5 lg:px-6 py-3.5 lg:py-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-2xl lg:rounded-3xl font-bold text-sm lg:text-base shadow-xl shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
          >
            {updatingStatus === order.id ? 'Iniciando...' : 'Iniciar Entrega'}
          </button>
        )}
        {order.status === 'delivering' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onViewDetails(order)
            }}
            className="flex-1 px-5 lg:px-6 py-3.5 lg:py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl lg:rounded-3xl font-bold text-sm lg:text-base shadow-xl shadow-emerald-500/40 active:scale-95 transition-transform"
          >
            Completar Entrega
          </button>
        )}
      </div>
    </div>
  )
}

export default DeliveryDashboard
